import { useEffect } from 'react';
import { Outlet, NavLink, useNavigate, useLocation, Link } from 'react-router-dom';
import { LayoutDashboard, Calendar, Package, LogOut, Receipt, Users } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import ToastViewport from '@/components/ui/Toast';
import { cn } from '@/lib/utils';

export default function AdminLayout() {
  const { isAuthenticated, isAdmin, isLoading, signOut, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isLoginPage = location.pathname === '/admin/login';

  useEffect(() => {
    if (isLoading) return;
    if (!isAuthenticated) {
      if (!isLoginPage) navigate('/admin/login', { replace: true });
      return;
    }
    // Ja tem sessao valida: nao pode ficar preso no form de login — o Nhost
    // recusa signIn com "User is already signed in" e a tela trava.
    if (isAdmin) {
      if (isLoginPage) navigate('/admin', { replace: true });
      return;
    }
    if (!isLoginPage) navigate('/', { replace: true });
  }, [isAuthenticated, isAdmin, isLoading, isLoginPage, navigate]);

  if (isLoginPage) {
    return (
      <>
        <Outlet />
        <ToastViewport />
      </>
    );
  }

  if (isLoading || !isAuthenticated || !isAdmin) {
    return null;
  }

  const links = [
    { to: '/admin', label: 'Dashboard', icon: LayoutDashboard, exact: true },
    { to: '/admin/bookings', label: 'Reservas', icon: Calendar },
    { to: '/admin/payment-proofs', label: 'Comprovantes', icon: Receipt },
    { to: '/admin/packages', label: 'Pacotes', icon: Package },
    { to: '/admin/users', label: 'Usuários', icon: Users },
  ];

  return (
    <div className="min-h-screen flex flex-col md:flex-row bg-sand-50">
      <aside className="md:w-60 md:min-h-screen md:border-r md:border-neutral-200 bg-white">
        <div className="p-6">
          <Link to="/admin" className="font-display text-lg text-ink-900">
            Destination<span className="italic"> Paradis</span>
            <p className="text-xs text-neutral-500 normal-case font-sans mt-1">Admin</p>
          </Link>
        </div>
        <nav className="px-2">
          {links.map(({ to, label, icon: Icon, exact }) => (
            <NavLink
              key={to}
              to={to}
              end={exact}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-3 px-4 py-3 rounded-md text-sm font-medium transition-colors',
                  isActive ? 'bg-teal-100 text-teal-600' : 'text-ink-700 hover:bg-sand-100',
                )
              }
            >
              <Icon size={18} /> {label}
            </NavLink>
          ))}
        </nav>
        <div className="md:absolute md:bottom-0 md:w-60 p-4 border-t border-neutral-200 mt-auto">
          <p className="text-xs text-neutral-500 mb-2">{user?.email}</p>
          <button
            onClick={() => { void signOut(); navigate('/admin/login'); }}
            className="flex items-center gap-2 text-sm text-ink-700 hover:text-red-600"
          >
            <LogOut size={16} /> Sair
          </button>
        </div>
      </aside>

      <main className="flex-1 p-6 md:p-12">
        <Outlet />
      </main>
      <ToastViewport />
    </div>
  );
}
